import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame } from "lucide-react";
import api from "../lib/api";
import Reveal from "./Reveal";

export default function InkWallsGallery() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/ink-walls").then((r) => setData(r.data)).catch(() => setData({ stories: [], community: [] }));
  }, []);

  if (!data || (!data.stories.length && !data.community.length)) return null;

  return (
    <section data-testid="ink-walls-gallery" className="mt-14">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-3">Fresh ink & walls</p>
            <h2 className="font-display font-extrabold uppercase text-2xl sm:text-3xl tracking-tight">
              New work, straight off <span className="font-serif italic normal-case text-ash">the chair & the wall</span>
            </h2>
          </div>
          <p className="text-ash text-[11px] font-mono uppercase tracking-[0.18em]">Refreshed as new pieces land</p>
        </div>
      </Reveal>
      {data.spotlight && (
        <Reveal>
          <Link to={`/article/${data.spotlight.slug}`} data-testid="artist-spotlight-card"
            className="group grid lg:grid-cols-2 border border-kinetic/30 bg-white/[0.02] hover:border-kinetic/60 transition-colors duration-300 mb-8 overflow-hidden">
            <div className="aspect-[16/10] lg:aspect-auto lg:min-h-[320px] overflow-hidden">
              <img src={data.spotlight.image_url} alt={data.spotlight.title} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-6 lg:p-10 flex flex-col justify-center">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-kinetic mb-3">Artist of the week</p>
              <h3 className="font-display font-extrabold uppercase text-2xl lg:text-3xl tracking-tight leading-tight">
                {data.spotlight.spotlight?.artist || data.spotlight.title}
              </h3>
              {data.spotlight.spotlight && (
                <p className="text-ash text-[11px] font-mono uppercase tracking-[0.2em] mt-3">
                  {data.spotlight.spotlight.city} · {data.spotlight.spotlight.style}
                </p>
              )}
              <p className="text-ash text-sm leading-relaxed mt-4">{data.spotlight.excerpt}</p>
              <span className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-kinetic group-hover:text-porcelain transition-colors">
                Read the profile →
              </span>
            </div>
          </Link>
        </Reveal>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {data.stories.map((s, i) => (          <Reveal key={s.slug} delay={Math.min(i, 5) * 0.05}>
            <Link to={`/article/${s.slug}`} data-testid={`ink-wall-story-${i}`}
              className="group relative block aspect-[4/5] overflow-hidden border border-white/10 hover:border-kinetic/50 transition-colors duration-300">
              <img src={s.image_url} alt={s.title} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/25 to-transparent" />
              <span className="absolute top-3 left-3 bg-kinetic text-obsidian text-[9px] font-mono uppercase tracking-[0.18em] px-2.5 py-1">Story</span>
              <p className="absolute bottom-0 inset-x-0 p-4 text-sm font-medium text-porcelain leading-snug">{s.title}</p>
            </Link>
          </Reveal>
        ))}
        {data.community.map((m, i) => (
          <Reveal key={m.id} delay={Math.min(i + 2, 5) * 0.05}>
            <a href={m.image_url} target="_blank" rel="noopener noreferrer" data-testid={`ink-wall-community-${i}`}
              className="group relative block aspect-[4/5] overflow-hidden border border-white/10 hover:border-kinetic/50 transition-colors duration-300">
              <img src={m.image_url} alt={`Shared by ${m.author_name}`} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/25 to-transparent" />
              <span className="absolute top-3 left-3 bg-cobalt text-porcelain text-[9px] font-mono uppercase tracking-[0.18em] px-2.5 py-1">From the Lounge</span>
              <div className="absolute bottom-0 inset-x-0 p-4 flex items-end justify-between gap-2">
                <p className="text-sm font-medium text-porcelain truncate">{m.author_name}</p>
                {(m.flames || 0) > 0 && (
                  <span className="flex items-center gap-1 text-[10px] font-mono text-kinetic shrink-0">
                    <Flame size={11} fill="currentColor" /> {m.flames}
                  </span>
                )}
              </div>
            </a>
          </Reveal>
        ))}
      </div>

      {data.spotlights && data.spotlights.length > 0 && (
        <Reveal>
          <div className="mt-12">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-ash mb-4">Artists of the week — every profile the desk has run</p>
            <div data-testid="spotlight-history" className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
              {data.spotlights.map((s, i) => (
                <Link key={s.slug} to={`/article/${s.slug}`} data-testid={`spotlight-history-${i}`}
                  className="group relative shrink-0 w-44 aspect-[4/5] overflow-hidden border border-white/10 hover:border-kinetic/50 transition-colors duration-300">
                  <img src={s.image_url} alt={s.spotlight?.artist || s.title} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <p className="font-display font-bold text-sm text-porcelain uppercase leading-tight">{s.spotlight?.artist || s.title}</p>
                    {s.spotlight?.city && <p className="text-[9px] font-mono uppercase tracking-[0.12em] text-ash mt-1">{s.spotlight.city}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      )}
    </section>
  );
}
